import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Briefcase, Calendar, User, Truck } from 'lucide-react';
import { Job, Vehicle, Driver } from '../../types';
import { jobApi, vehicleApi, driverApi } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import LoadingSpinner from '../ui/LoadingSpinner';
import JobForm from './JobForm';

const JobManagement: React.FC = () => {
  const { isCompanyStaff, isClientUser, getClientId } = useAuthStore();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    fetchJobs();
    if (isCompanyStaff()) {
      fetchVehicles();
      fetchDrivers();
    }
  }, [isCompanyStaff]);

  const fetchJobs = async () => {
    try {
      const response = await jobApi.getAll();
      if (response.success && response.data) {
        setJobs(response.data);
      }
    } catch (err) {
      setError('Failed to fetch jobs');
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await vehicleApi.getAll();
      if (response.success && response.data) {
        setVehicles(response.data);
      }
    } catch (err) {
      console.warn('Failed to fetch vehicles');
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await driverApi.getAll();
      if (response.success && response.data) {
        setDrivers(response.data);
      }
    } catch (err) {
      console.warn('Failed to fetch drivers');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateJobStatus = async (jobId: number, status: string) => {
    try {
      const response = await jobApi.updateStatus(jobId, status);
      if (response.success && response.data) {
        setJobs(jobs.map(j => j.id === jobId ? response.data! : j));
      }
    } catch (err) {
      setError('Failed to update job status');
    }
  };

  const handleDeleteJob = async (id: number) => {
    if (!confirm('Are you sure you want to delete this job?')) return;
    
    try {
      const response = await jobApi.delete(id);
      if (response.success) {
        setJobs(jobs.filter(j => j.id !== id));
      }
    } catch (err) {
      setError('Failed to delete job');
    }
  };

  const filteredJobs = jobs.filter(job => {
    return filterStatus === 'all' || job.status === filterStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getVehicleName = (vehicleId: number) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? vehicle.name : 'Unknown Vehicle';
  };

  const getDriverName = (driverId: number) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver ? driver.name : 'Unknown Driver';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-fleet-dark">Job Management</h1>
          <p className="text-fleet-neutral">
            {isClientUser() ? "View your assigned jobs" : "Assign and track jobs for your fleet"}
          </p>
        </div>
        {isCompanyStaff() && (
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create Job
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-fleet p-4">
        <div className="flex items-center space-x-4">
          <Briefcase className="h-5 w-5 text-fleet-neutral" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-fleet-primary focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800 ml-4"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Jobs List */}
      <div className="bg-white rounded-lg shadow-fleet overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-fleet-dark">Active Jobs</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {filteredJobs.map((job) => (
            <div key={job.id} className="p-6 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-4 mb-2">
                    <h4 className="text-lg font-medium text-fleet-dark">{job.title}</h4>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(job.status)}`}>
                      {job.status.replace('_', ' ')}
                    </span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(job.priority)}`}>
                      {job.priority} priority
                    </span>
                  </div>
                  
                  <p className="text-fleet-neutral mb-4">{job.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center">
                      <Truck className="h-4 w-4 text-fleet-neutral mr-2" />
                      <span>{getVehicleName(job.vehicle_id)}</span>
                    </div>
                    <div className="flex items-center">
                      <User className="h-4 w-4 text-fleet-neutral mr-2" />
                      <span>{getDriverName(job.driver_id)}</span>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-fleet-neutral mr-2" />
                      <span>{new Date(job.scheduled_start).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {job.status === 'pending' && (
                    <button
                      onClick={() => handleUpdateJobStatus(job.id, 'in_progress')}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Start
                    </button>
                  )}
                  {job.status === 'in_progress' && (
                    <button
                      onClick={() => handleUpdateJobStatus(job.id, 'completed')}
                      className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Complete
                    </button>
                  )}
                  {isCompanyStaff() && (
                    <>
                      <button
                        onClick={() => setEditingJob(job)}
                        className="text-fleet-primary hover:text-fleet-secondary"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteJob(job.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Job Form Modal - only for company staff */}
      {isCompanyStaff() && (showAddModal || editingJob) && (
        <JobForm
          job={editingJob}
          vehicles={vehicles}
          drivers={drivers}
          onSubmit={async (jobData: Partial<Job>) => {
            try {
              // Add client_id - use logged-in user's client or default to 1
              const clientId = getClientId() || 1;
              const dataWithClient = {
                ...jobData,
                client_id: clientId,
              };
              
              if (editingJob) {
                const response = await jobApi.update(editingJob.id, dataWithClient);
                if (response.success && response.data) {
                  setJobs(jobs.map(j => j.id === editingJob.id ? response.data! : j));
                  setEditingJob(null);
                }
              } else {
                const response = await jobApi.create(dataWithClient);
                if (response.success && response.data) {
                  setJobs([...jobs, response.data]);
                  setShowAddModal(false);
                }
              }
              setError(null);
            } catch (err) {
              setError(`Failed to ${editingJob ? 'update' : 'create'} job`);
            }
          }}
          onCancel={() => {
            setShowAddModal(false);
            setEditingJob(null);
          }}
        />
      )}
    </div>
  );
};

export default JobManagement;